import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { Autocomplete } from 'app/agriculture/forms/components/Autocomplete/Autocomplete';
import { Select } from 'app/agriculture/forms/components/Select/Select';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { useHerdCreateMutation } from 'app/agriculture/mutations/herds/useHerdCreateMutation';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { AnimalSpeciesEnum, getHerdType } from 'app/agriculture/models/entities/herd/HerdAnimals';
import { useHerds } from 'app/agriculture/queries/herds/useHerds';
import { isDate, isFuture } from 'date-fns';
import { HerdIcon } from 'app/agriculture/icons';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { HerdCreateFields } from './HerdCreate';
import { validateHerdTotalAnimals } from '../utils';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { useInitialParent } from 'app/agriculture/router/useInitialParent';
import { useAttachOptions } from 'app/agriculture/hooks/useAttachOptions/useAttachOptions';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { DatePickerInput } from 'app/agriculture/forms/components/DatePicker/DatePickerInput';

export function HerdCreateForm() {
    const { timeZone } = useTimeZone();
    const navigate = useNavigate();
    const routeActions = useRouteActions();
    const herdCount = useHerds().size;
    const namePlaceholder = `Herd ${(herdCount + 1).toString()}`;
    const { id: initialParentId = null } = useInitialParent() ?? {};
    const formController = useForm<HerdCreateFields>({
        mode: 'all',
        defaultValues: {
            animalSpecies: 'Cow',
            herdType: 'Cattle',
            snapshot: {
                totalAnimals: 1,
                paddockId: initialParentId,
                timestamp: new TZDate(new Date(), timeZone),
            },
        },
        shouldFocusError: true,
    });

    const {
        register,
        watch,
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = formController;

    const { agricultureToastService } = useContext(ServicesContext);
    const { mutate, isPending } = useHerdCreateMutation({
        onSuccess: ({ id }) => {
            agricultureToastService
                .showToast({
                    title: 'Herd has been saved',
                    subtext: 'The herd was saved successfully!',
                    type: AgricultureToastType.SUCCESS,
                    variant: 'detailed',
                    actions: [
                        {
                            text: 'Add Another Herd',
                            onClick: () => {
                                routeActions.create('Herd');
                            },
                            startIcon: 'add',
                        },
                        {
                            text: 'Edit Herd',
                            onClick: () => {
                                routeActions.edit(id, { type: 'Herd' });
                            },
                            startIcon: 'edit',
                            variant: 'textonly',
                        },
                    ],
                })
                .catch((e: unknown) => {
                    console.error(e);
                });
            reset();
            routeActions.goto(id, { type: 'Herd' });
        },
        onError: (error) => {
            console.error('Error creating herd', error);
            agricultureToastService
                .showToast({
                    title: 'Herd Create Failed:',
                    subtext: 'Something went wrong',
                    type: AgricultureToastType.ERROR,
                    variant: 'simple',
                })
                .then()
                .catch((e: unknown) => {
                    console.error(e);
                });
        },
    });

    const speciesOptions = AnimalSpeciesEnum.options.map((value) => {
        return { id: value, name: value };
    });

    const herdTypeOptions = getHerdType(watch('animalSpecies'))?.map((value) => {
        return { id: value, name: value };
    });
    const onSubmit: SubmitHandler<HerdCreateFields> = ({ name, herdType, ...formData }) => {
        if (formData.snapshot.timestamp == null) throw new Error('no timestamp');

        mutate({
            ...formData,
            herdType: herdTypeOptions ? herdType : null,
            name: name === '' ? namePlaceholder : name,
            snapshot: {
                ...formData.snapshot,
                timestamp: formData.snapshot.timestamp.toISOString(),
            },
        });
    };

    const attachOptions = useAttachOptions(['Paddock']);

    return (
        <ContentBox title="Create Herd" className="NuHerdCreateForm" Icon={HerdIcon} variant={'md'}>
            <FormProvider {...formController}>
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section scrollable className="NuHerdCreateForm__fields">
                        <ContentBox.Section className="NuHerdCreateForm__fields__time-static-container">
                            <Input
                                label="Herd name"
                                placeholder={namePlaceholder}
                                {...register('name')}
                                errorMessage={errors.name?.message}
                            />

                            <SimpleGrid
                                columns={2}
                                className="NuHerdCreateForm__fields__time-static-container__dropdowns"
                            >
                                <Select
                                    fieldName="animalSpecies"
                                    noun="Herd Animal"
                                    options={speciesOptions}
                                    formController={formController}
                                />
                                {herdTypeOptions ? (
                                    <Select
                                        fieldName="herdType"
                                        noun="Herd Type"
                                        options={herdTypeOptions}
                                        formController={formController}
                                    />
                                ) : null}
                            </SimpleGrid>
                        </ContentBox.Section>
                        <ContentBox.Section className="NuHerdCreateForm__fields__time-dependant-container">
                            <Controller
                                control={control}
                                name="snapshot.timestamp"
                                rules={{
                                    validate: {
                                        invalidDate: (value) => {
                                            if (value == null || !isDate(value)) {
                                                return 'Please enter a valid date';
                                            }
                                        },
                                        noFutureDate: (value) => {
                                            if (value == null) {
                                                return 'Please enter a valid date';
                                            }

                                            if (isFuture(value)) {
                                                return 'Please select a past date';
                                            }
                                        },
                                    },
                                }}
                                render={({ field, fieldState, formState }) => (
                                    <DatePickerInput
                                        {...field}
                                        label="Date"
                                        showTimeInput
                                        initialValue={formState.defaultValues?.snapshot?.timestamp}
                                        errorMessage={fieldState.error?.message}
                                        onChange={(date?: Date) => {
                                            if (date == null) {
                                                field.onChange();
                                                return;
                                            }

                                            field.onChange(date);
                                        }}
                                    />
                                )}
                            />
                            <SimpleGrid columns={2}>
                                <Autocomplete
                                    fieldName="snapshot.paddockId"
                                    options={attachOptions}
                                    noun={'Herd location'}
                                    formController={formController}
                                />
                                <Input
                                    label="Total animals"
                                    {...register('snapshot.totalAnimals', {
                                        validate: validateHerdTotalAnimals,
                                    })}
                                    errorMessage={errors.snapshot?.totalAnimals?.message}
                                    type="number"
                                    step={1}
                                    min={1}
                                />
                            </SimpleGrid>
                        </ContentBox.Section>
                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Create herd',
                                type: 'submit',
                                isLoading: isPending,
                            }}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
